import { resolveMediaUrl } from "../../../utils/resolveMediaUrl";

export function buildPropertyAssetImageUrl(path) {
  return resolveMediaUrl(path);
}

/** Last path segment for display on edit (browser file inputs cannot show stored paths). */
export function getPropertyAssetImageFileName(path) {
  if (!path || typeof path !== "string") return "";
  const withoutQuery = path.split("?")[0];
  const segment = withoutQuery.split("/").filter(Boolean).pop();
  return segment || path;
}

export function findPropertyStatusId(statuses, keyword) {
  const k = String(keyword ?? "").trim().toLowerCase();
  if (!k) return null;
  const row = (statuses || []).find((s) =>
    String(s.status_name ?? s.label ?? "").toLowerCase().includes(k),
  );
  return row?.id ?? row?.value ?? null;
}

export function formatAssetDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB");
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * @param {object} row — asset record
 * @param {{ fields?: { label: string, value: string }[], showImage?: boolean }} options
 */
export function printPropertyAsset(row, { fields = [], showImage = false } = {}) {
  const w = window.open("", "_blank", "width=800,height=900");
  if (!w) return;

  const title = escapeHtml(row.asset_code || row.id || "Asset");
  const printedAt = escapeHtml(new Date().toLocaleString("en-IN"));

  const tableBody =
    fields.length > 0
      ? fields
          .map(
            (f) =>
              `<tr><th>${escapeHtml(f.label)}</th><td>${escapeHtml(f.value)}</td></tr>`,
          )
          .join("")
      : `<tr><td colspan="2">No columns selected. Enable columns in Show columns, then print again.</td></tr>`;

  const imageUrl =
    showImage && row.asset_image_url
      ? escapeHtml(buildPropertyAssetImageUrl(row.asset_image_url))
      : "";

  const imageBlock = imageUrl
    ? `<figure class="print-asset-image">
        <figcaption>Asset image</figcaption>
        <img
          src="${imageUrl}"
          alt="${escapeHtml(row.property_asset_name || "Asset")}"
          onerror="this.replaceWith(Object.assign(document.createElement('div'),{textContent:'Image not available',style:'padding:1rem;color:#64748b;border:1px solid #e2e8f0;border-radius:0.5rem;background:#f8fafc'}))"
        />
      </figure>`
    : "";

  const html = `
    <!DOCTYPE html>
    <html><head><title>Asset ${title}</title>
    <style>
      body { font-family: system-ui, sans-serif; padding: 2rem; color: #111; }
      h1 { font-size: 1.25rem; margin-bottom: 0.5rem; }
      table { width: 100%; border-collapse: collapse; margin-top: 1rem; }
      th, td { text-align: left; padding: 0.5rem; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
      th { width: 35%; color: #374151; font-weight: 600; }
      td { word-break: break-word; }
      .print-asset-image { margin: 1.25rem 0 0; padding: 0; }
      .print-asset-image figcaption {
        margin: 0 0 0.5rem;
        font-size: 0.75rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #64748b;
      }
      .print-asset-image img {
        display: block;
        max-width: 10.5rem;
        max-height: 10.5rem;
        object-fit: contain;
        border-radius: 0.5rem;
        border: 1px solid #e2e8f0;
      }
      @media print { body { padding: 0; } }
    </style></head><body>
    <h1>Property / Asset — ${title}</h1>
    <p style="color:#6b7280;margin:0;">Printed ${printedAt}</p>
    <table>${tableBody}</table>
    ${imageBlock}
    <script>
      window.onload = () => {
        const img = document.querySelector(".print-asset-image img");
        const runPrint = () => window.print();
        if (img && !img.complete) {
          img.onload = runPrint;
          img.onerror = runPrint;
        } else {
          runPrint();
        }
      };
    </script>
    </body></html>`;

  w.document.write(html);
  w.document.close();
}
